/**
 * Request/response DTOs for the auth module. These mirror what the API
 * actually returns; if the API ever changes its envelope, only this file
 * and `auth.service.ts` need to change.
 */

export interface DevLoginRequest {
    email:    string;
    password: string;
}

export interface LoginPlan {
    plan_code:           string | null;
    /**
     * Catalog client type: 'solo' | 'team' | 'developer' | 'builder'. Decides
     * which column set of the pricing table applies — see `isRealtorPlan` in
     * shared/access/plan-gates.
     */
    client_type:         string | null;
    tier_code:           string | null;
    name:                string;
    description:         string | null;
    category:            string;
    monthly_cents:       number | null;
    annual_cents:        number | null;
    billing_cycle:       string;
    subscription_status: string;
    current_period_end:  string | null;
    limits: {
        properties:      number | null;
        qr_codes:        number | null;
        scans_per_month: number | null;
        leads_visible:   number | null;
        sms_per_month:   number | null;
        email_per_month: number | null;
        dashboard_users: number | null;
        storage_mb:      number | null;
    };
    features: {
        website_widget:        boolean;
        analytics_integration: boolean;
        email_marketing:       boolean;
        social_analytics:      boolean;
        crm_integration:       boolean;
        custom:                boolean;
        api_access:            boolean;
    };
    is_contact_sales: boolean;
}

import type { DashboardRole } from '@/shared/types/session';
import type { PermissionMap } from '@/shared/permissions';

export interface DevLoginResponse {
    token:  string;
    user:   { id: string; email: string; full_name: string };
    tenant: { id: string; slug: string; name: string; role: DashboardRole };
    plan:   LoginPlan | null;
    /** The member's delegated permissions; null for owners (full access). */
    permissions: PermissionMap | null;
}

/** Returned by /auth/login when the account has 2FA on — no session yet. */
export interface MfaChallengeResponse {
    mfa_required:    true;
    challenge_token: string;
    method:          'google' | 'microsoft' | 'email' | null;
    email:           string;
    /** Email method only: false when the code could not be mailed — fall back to a backup code. */
    code_sent?:      boolean;
}

/** /auth/login resolves to a full session OR a 2FA challenge. */
export type LoginResult = DevLoginResponse | MfaChallengeResponse;

/** Narrowing helper so call sites read clearly. */
export function isMfaChallenge(r: LoginResult): r is MfaChallengeResponse {
    return 'mfa_required' in r && r.mfa_required === true;
}

/** POST /auth/login/verify-2fa — exchange challenge + code for a session. */
export interface VerifyTwoFactorRequest {
    challenge_token: string;
    code:            string;
}

/** POST /auth/login/resend-2fa — mail a fresh code (email method only). */
export interface ResendTwoFactorRequest {
    challenge_token: string;
}

export interface ForgotPasswordRequest {
    email: string;
}

export interface ResetPasswordRequest {
    token:    string;
    password: string;
}
